import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../lib/api";
import { clearAuth, setAuthState } from "../store/authSlice";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [user, setUserState] = useState(auth.user);
  const [profile, setProfileState] = useState(auth.profile);
  const [loading, setLoading] = useState(auth.loading);

  const syncAuth = useCallback((nextUser, nextProfile, nextLoading = false) => {
    setUserState(nextUser);
    setProfileState(nextProfile);
    setLoading(nextLoading);
    dispatch(setAuthState({ user: nextUser, profile: nextProfile, loading: nextLoading }));
  }, [dispatch]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      syncAuth(data.user, data.profile, false);
    } catch {
      syncAuth(null, null, false);
    } finally {
      setLoading(false);
    }
  }, [syncAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    dispatch(clearAuth());
    setUserState(null);
    setProfileState(null);
    setLoading(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        refresh,
        setUser: (next) => syncAuth(next, profile, false),
        setProfile: (next) => syncAuth(user, next, false),
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
